import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ProcessedAttribute,
  processAttributes,
  isAttributeListValid,
  createNewAttribute,
} from '../utils/attributeValidation';
import { AttributeItem } from '../AiAttributesList/aiAttributeList.types';
import { useAiScoreAttributeMutations } from '../AiAttributesList/api/useAiScoreAttributeMutations';
import { useShowToast } from '@/hooks';
import { isEqual } from 'lodash-es';
import { AiScoringConstants } from '../aiScoringConstants';

const {
  MIN_ATTRIBUTE_LENGTH,
  MIN_ATTRIBUTE_LENGTH_MESSAGE,
  DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE,
} = AiScoringConstants;

export interface UseAiScoringAttributesProps {
  initialAttributes: AttributeItem[];
  jobId: string;
  onValidationComplete?: (result: any) => void;
}

export interface UseAiScoringAttributesReturn {
  attributes: ProcessedAttribute[];
  isDirty: boolean;
  isValid: boolean;
  isLoading: boolean;
  hasValidatedButNotSaved: boolean;
  interactedAttributes: Set<number>;

  handleAttributeChange: (id: number, newDescription: string) => void;
  handleAttributeBlur: (id: number) => void;
  handleAttributeDelete: (id: number) => void;
  handleAttributeAdd: () => { newAttributeId: number };
  handleAttributeReorder: (
    sourceIndex: number,
    destinationIndex: number
  ) => void;
  saveAttributes: (
    shouldSave?: boolean,
    retrigger?: boolean,
    onSuccess?: () => void
  ) => Promise<any>;
  resetDirtyState: () => void;
  setValidatedButNotSaved: (value: boolean) => void;
  handleAttributeAutoFocus: (id: number) => void;
}

export const useAiScoringAttributes = ({
  initialAttributes,
  jobId,
  onValidationComplete,
}: UseAiScoringAttributesProps): UseAiScoringAttributesReturn => {
  // Store previous initialAttributes to detect actual changes
  const prevInitialAttributesRef = useRef<AttributeItem[]>(initialAttributes);

  // Central state for all attributes - single source of truth
  const [attributes, setAttributes] = useState<ProcessedAttribute[]>(() => {
    return processAttributes(initialAttributes);
  });

  // Dirty state tracking - now computed based on meaningful changes
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Track if attributes have been validated after AI scoring explained but not yet saved
  const [hasValidatedButNotSaved, setHasValidatedButNotSaved] = useState(false);

  // Track which attributes have been interacted with (focused + blurred at least once)
  const [interactedAttributes, setInteractedAttributes] = useState<Set<number>>(
    new Set()
  );

  // Track validation debounce timers
  const validationTimersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Track original attributes to determine what has actually changed
  const [originalAttributes, setOriginalAttributes] = useState<
    Map<number, ProcessedAttribute>
  >(() => {
    const map = new Map<number, ProcessedAttribute>();
    const processed = processAttributes(initialAttributes);
    processed.forEach((attr) => {
      map.set(attr.id, { ...attr });
    });
    return map;
  });

  const toast = useShowToast();

  // API mutations hook
  const { validateAttributesMutation, isLoading } =
    useAiScoreAttributeMutations({
      jobData: { id: jobId },
      setFinalValidationResult: (result: any) => {
        if (result?.attributes) {
          const processedAttributes = processAttributes(result.attributes);
          setAttributes(processedAttributes);
          // Set hasValidatedButNotSaved to true when validation completes
          setHasValidatedButNotSaved(true);
        }
        // Call the validation complete callback
        if (onValidationComplete) {
          onValidationComplete(result);
        }
      },
    });

  // Memoized validation states
  const isValid = useMemo(() => {
    return isAttributeListValid(attributes, interactedAttributes);
  }, [attributes, interactedAttributes]);

  // Memoize processed initial attributes
  const processedInitialAttributes = useMemo(() => {
    return processAttributes(initialAttributes);
  }, [initialAttributes]);

  // Memoize whether initial attributes have changed
  const hasInitialAttributesChanged = useMemo(() => {
    return !isEqual(initialAttributes, prevInitialAttributesRef.current);
  }, [initialAttributes]);

  // Update attributes when initial data changes using callback
  const updateAttributesFromInitial = useCallback(() => {
    if (hasInitialAttributesChanged) {
      prevInitialAttributesRef.current = initialAttributes;

      setAttributes(processedInitialAttributes);

      // Update original attributes map
      const newOriginalMap = new Map<number, ProcessedAttribute>();
      processedInitialAttributes.forEach((attr) => {
        newOriginalMap.set(attr.id, { ...attr });
      });
      setOriginalAttributes(newOriginalMap);

      // Reset interaction state when new data comes in
      setUserHasInteracted(false);
      setHasValidatedButNotSaved(false);
    }
  }, [
    hasInitialAttributesChanged,
    initialAttributes,
    processedInitialAttributes,
  ]);

  // Call update when conditions change
  useEffect(() => {
    updateAttributesFromInitial();
  }, [updateAttributesFromInitial]);

  // Cleanup validation timers on unmount - keep this useEffect as it's necessary for cleanup
  useEffect(() => {
    const timersToCleanup = validationTimersRef.current;
    return () => {
      timersToCleanup.forEach((timer) => clearTimeout(timer));
      timersToCleanup.clear();
    };
  }, []);

  // Compute isDirty based on meaningful changes using memoized comparison
  const isDirty = useMemo(() => {
    if (!userHasInteracted) return false;

    // Create current state snapshot (only non-new attributes with trimmed descriptions)
    const currentNonNewAttrs = attributes
      .filter((attr) => !attr.isNewAttribute)
      .map((attr) => ({
        id: attr.id,
        description: attr.description.trim(),
        position: attr.position,
      }))
      .sort((a, b) => a.id - b.id); // Sort for consistent comparison

    // Create original state snapshot (trimmed descriptions)
    const originalAttrs = Array.from(originalAttributes.values())
      .map((attr) => ({
        id: attr.id,
        description: attr.description.trim(),
        position: attr.position,
      }))
      .sort((a, b) => a.id - b.id); // Sort for consistent comparison

    // Check for new non-empty attributes
    const hasNewNonEmptyAttributes = attributes.some(
      (attr) => attr.isNewAttribute && attr.description.trim().length > 0
    );

    // Check if current and original arrays are different in length
    const hasLengthChange = currentNonNewAttrs.length !== originalAttrs.length;

    // Check if any existing attribute has been modified (content or position)
    const hasModifications = currentNonNewAttrs.some((currentAttr, index) => {
      const originalAttr = originalAttrs[index];
      if (!originalAttr || originalAttr.id !== currentAttr.id) return true;

      return (
        originalAttr.description !== currentAttr.description ||
        originalAttr.position !== currentAttr.position
      );
    });

    return hasNewNonEmptyAttributes || hasLengthChange || hasModifications;
  }, [userHasInteracted, attributes, originalAttributes]);

  /**
   * CORE VALIDATION FUNCTION
   * Validates a single attribute and updates its validation state
   * This is used by both debounced validation and immediate blur validation
   */
  const validateSingleAttribute = useCallback(
    (
      id: number,
      newDescription: string,
      validationType: 'onChange' | 'onBlur' = 'onBlur',
      overrideInteractionState?: boolean
    ) => {
      setAttributes((prev) => {
        // STEP 1: UPDATE ONLY THE CHANGED ATTRIBUTE'S DESCRIPTION
        // Create new array with only the target attribute's description updated
        // All other attributes remain completely unchanged (description + validation state)
        const updatedAttrs = prev.map((attr) =>
          attr.id === id ? { ...attr, description: newDescription } : attr
        );

        // STEP 2: LOCATE AND VALIDATE ONLY THE CHANGED ATTRIBUTE
        // Find the index of the changed attribute for targeted validation
        const changedAttrIndex = updatedAttrs.findIndex(
          (attr) => attr.id === id
        );
        if (changedAttrIndex === -1) return prev; // Safety check: attribute not found

        const changedAttr = updatedAttrs[changedAttrIndex];
        const desc = changedAttr.description?.trim() ?? '';

        // VALIDATION CHECK 1: DUPLICATE DETECTION
        // Compare ONLY with other attributes (exclude self to avoid false positive)
        const hasDuplicate = updatedAttrs.some((attr, index) => {
          if (index === changedAttrIndex) return false; // Skip self-comparison
          const attrDesc = attr.description?.trim() ?? '';
          const isMatch = attrDesc === desc && desc.length > 0;

          return isMatch;
        });

        // VALIDATION CHECK 2: MINIMUM LENGTH REQUIREMENT
        // Apply business rules for attribute validity

        // Start with the existing validation state to preserve API errors
        const existingAttr = prev.find((attr) => attr.id === id);
        let is_valid = existingAttr?.is_valid ?? true;
        let reason = existingAttr?.reason ?? '';
        let valid_type: 'success' | 'error' | 'warning' =
          existingAttr?.valid_type ?? 'success';

        // For new attributes that haven't been interacted with, show neutral state
        const hasBeenInteracted =
          overrideInteractionState ?? interactedAttributes.has(id);

        const isNewEmptyAttribute =
          changedAttr.isNewAttribute && desc.trim().length === 0;

        // Enhanced Validation Logic based on validation type:
        // 1. onChange (while typing): Only show duplicate errors, be lenient with min length
        // 2. onBlur (after interaction): Show both duplicate and min length errors
        // 3. Brand new empty attributes: Stay neutral until interaction
        // 4. PRESERVE existing API validation errors when user hasn't interacted

        // Simplified validation logic - clear and predictable rules
        const shouldShowErrors = (() => {
          // Special case: New empty attributes that were auto-focused but user blurred without typing
          // This happens when user adds new attribute (auto-focuses) but immediately clicks away
          if (
            changedAttr.isNewAttribute &&
            desc.length === 0 &&
            validationType === 'onBlur' &&
            hasBeenInteracted
          ) {
            return true; // Show error to guide user to add content
          }

          // New empty attributes: no errors until user types something meaningful
          if (changedAttr.isNewAttribute && desc.length === 0) {
            return false;
          }

          // New attributes with minimal content: no errors while typing (onChange)
          if (
            changedAttr.isNewAttribute &&
            desc.length < 3 &&
            validationType === 'onChange'
          ) {
            return false;
          }

          // Everything else: show errors if user has interacted OR we're forcing validation (onBlur with override)
          return hasBeenInteracted || desc.length >= 3;
        })();

        if (!shouldShowErrors) {
          // Keep neutral state - don't show any errors
          is_valid = true;
          reason = '';
          valid_type = 'success';
        } else {
          // Apply validation based on the validation type
          if (validationType === 'onChange') {
            // While typing: clear API errors immediately, only show duplicate errors
            const originalFromMap = originalAttributes.get(id);
            const originalDesc = originalFromMap?.description?.trim() ?? '';
            const isBackToOriginal = desc === originalDesc;
            const wasModifiedFromOriginal = !isBackToOriginal;

            // Handle API error/warning clearing while typing
            if (
              originalFromMap &&
              (originalFromMap.valid_type === 'error' ||
                originalFromMap.valid_type === 'warning') &&
              !originalFromMap.is_valid
            ) {
              if (isBackToOriginal) {
                // Back to original - restore API error/warning

                is_valid = originalFromMap.is_valid;
                reason = originalFromMap.reason;
                valid_type = originalFromMap.valid_type;
              } else if (wasModifiedFromOriginal) {
                // Modified from original - clear API error/warning immediately

                is_valid = true;
                reason = '';
                valid_type = 'success';

                // Only show duplicate errors while typing, not length errors
                if (hasDuplicate) {
                  is_valid = false;
                  reason = DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE;
                  valid_type = 'error';
                }
              }
            } else {
              // No original API error - apply duplicate validation
              if (hasDuplicate) {
                is_valid = false;
                reason = DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE;
                valid_type = 'error';
              } else {
                // Clear any existing duplicate errors
                is_valid = true;
                reason = '';
                valid_type = 'success';
              }
            }
            // Don't show min length errors while typing - better UX
          } else if (validationType === 'onBlur') {
            // On blur: show both duplicate and min length errors
            // But only if the attribute has meaningful content or has been interacted with

            // Check if content has been modified from original (to decide API error handling)
            const originalFromMap = originalAttributes.get(id);
            const originalDesc = originalFromMap?.description?.trim() ?? '';
            const isBackToOriginal = desc === originalDesc;
            const wasModifiedFromOriginal = !isBackToOriginal;

            // Handle API error/warning preservation/clearing logic
            if (
              originalFromMap &&
              (originalFromMap.valid_type === 'error' ||
                originalFromMap.valid_type === 'warning') &&
              !originalFromMap.is_valid
            ) {
              // This attribute originally had an API error or warning
              if (isBackToOriginal) {
                // Changed back to original - restore original API error/warning

                is_valid = originalFromMap.is_valid;
                reason = originalFromMap.reason;
                valid_type = originalFromMap.valid_type;
              } else if (wasModifiedFromOriginal) {
                // Content was modified from original - clear API error/warning and apply FE validation

                is_valid = true;
                reason = '';
                valid_type = 'success';

                // Apply frontend validation
                if (
                  (hasBeenInteracted &&
                    desc.trim().length < MIN_ATTRIBUTE_LENGTH) ||
                  isNewEmptyAttribute
                ) {
                  is_valid = false;
                  reason = MIN_ATTRIBUTE_LENGTH_MESSAGE;
                  valid_type = 'error';
                } else if (hasDuplicate) {
                  is_valid = false;
                  reason = DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE;
                  valid_type = 'error';
                } else {
                  // Keep the cleared state when no validation errors
                  // (is_valid, reason, valid_type already set to success above)
                }
              }
              // If not back to original and not modified (edge case), keep existing state
            } else {
              // No original API error OR attribute is valid - apply normal FE validation
              if (
                (hasBeenInteracted &&
                  desc.trim().length < MIN_ATTRIBUTE_LENGTH) ||
                isNewEmptyAttribute
              ) {
                is_valid = false;
                reason = MIN_ATTRIBUTE_LENGTH_MESSAGE;
                valid_type = 'error';
              } else if (hasDuplicate) {
                is_valid = false;
                reason = DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE;
                valid_type = 'error';
              } else {
                // Clear any existing errors when validation passes
                is_valid = true;
                reason = '';
                valid_type = 'success';
              }
            }
          }
        }
        // All other cases remain valid

        // STEP 3: UPDATE CHANGED ATTRIBUTE AND CHECK FOR DUPLICATE CLEARANCE
        // First, update the changed attribute with its new validation state

        const result = updatedAttrs.map(
          (attr) =>
            attr.id === id
              ? {
                  ...attr,
                  is_valid,
                  reason,
                  valid_type,
                }
              : attr // Keep other attributes unchanged initially
        );

        // STEP 4: HANDLE DUPLICATE ERROR CLEARANCE (EDGE CASE FIX)
        // If the changed attribute no longer has the same value as others,
        // we need to clear duplicate errors from other attributes that might
        // have been flagged as duplicates of the old value
        const oldDescription =
          prev.find((attr) => attr.id === id)?.description?.trim() ?? '';

        // If the description actually changed, check if we need to clear duplicate errors
        if (oldDescription !== desc) {
          // Find other attributes that have duplicate errors for the OLD description
          const attributesToClearDuplicates = result.filter(
            (attr) =>
              attr.id !== id && // Not the changed attribute
              attr.valid_type === 'error' &&
              attr.reason === DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE &&
              attr.description?.trim() === oldDescription
          );

          // If there are attributes with duplicate errors for the old value,
          // check if they still have duplicates with the remaining attributes
          if (attributesToClearDuplicates.length > 0) {
            return result.map((attr) => {
              // Skip if not in the list of attributes to potentially clear
              if (
                !attributesToClearDuplicates.some(
                  (clearAttr) => clearAttr.id === attr.id
                )
              ) {
                return attr;
              }

              // Check if this attribute still has duplicates with OTHER attributes
              // (excluding the one we just changed)
              const attrDesc = attr.description?.trim() ?? '';
              const stillHasDuplicates = result.some((otherAttr) => {
                return (
                  otherAttr.id !== attr.id && // Not the same attribute
                  otherAttr.id !== id && // Not the one we just changed
                  otherAttr.description?.trim() === attrDesc &&
                  attrDesc.length > 0
                );
              });

              // If no duplicates remain, clear the duplicate error
              if (!stillHasDuplicates) {
                // Re-validate for other potential errors (like min length)
                const shouldShowErrorsForThis = interactedAttributes.has(
                  attr.id
                );
                if (shouldShowErrorsForThis && validationType === 'onBlur') {
                  // Only show min length errors on blur, not while typing
                  const newIsValid =
                    attrDesc.trim().length >= MIN_ATTRIBUTE_LENGTH;
                  return {
                    ...attr,
                    is_valid: newIsValid,
                    reason: newIsValid ? '' : MIN_ATTRIBUTE_LENGTH_MESSAGE,
                    valid_type: newIsValid ? 'success' : ('error' as const),
                  };
                } else {
                  // Don't show errors for non-interacted attributes or during onChange
                  return {
                    ...attr,
                    is_valid: true,
                    reason: '',
                    valid_type: 'success' as const,
                  };
                }
              }

              return attr; // Keep duplicate error if other duplicates still exist
            });
          }
        }

        return result;
      });
    },
    [interactedAttributes, originalAttributes]
  );

  /**
   * REAL-TIME CHANGE HANDLER WITH DEBOUNCED VALIDATION
   * Updates description immediately but delays validation to prevent jarring UX
   */
  const handleAttributeChange = useCallback(
    (id: number, newDescription: string) => {
      // Prevent spaces as the first character
      if (newDescription.startsWith(' ')) {
        return;
      }

      // Clear any existing validation timer for this attribute
      const existingTimer = validationTimersRef.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Update description immediately (no validation yet)
      setAttributes((prev) =>
        prev.map((attr) =>
          attr.id === id ? { ...attr, description: newDescription } : attr
        )
      );

      // Set up debounced validation (500ms delay)
      const timer = setTimeout(() => {
        validateSingleAttribute(id, newDescription, 'onChange'); // Don't force errors during typing
        validationTimersRef.current.delete(id);
      }, 500);

      validationTimersRef.current.set(id, timer);

      // STATE MANAGEMENT: Track changes and user interaction
      setUserHasInteracted(true); // Mark that user has made changes
      setHasValidatedButNotSaved(false); // Reset validation cache state
    },
    [validateSingleAttribute]
  );

  /**
   * BLUR HANDLER
   * Immediate validation when user leaves the field - this provides definitive feedback
   */
  const handleAttributeBlur = useCallback(
    (id: number) => {
      // Clear any pending debounced validation
      const existingTimer = validationTimersRef.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
        validationTimersRef.current.delete(id);
      }

      // Get current description for validation
      const currentAttr = attributes.find((attr) => attr.id === id);
      if (currentAttr) {
        const currentDescription = currentAttr.description;

        // Check if content actually changed from original
        const originalAttr = originalAttributes.get(id);
        const originalDescription = originalAttr?.description ?? '';
        const hasContentChanged =
          currentDescription.trim() !== originalDescription.trim();

        // Only mark as interacted if content actually changed
        const willBeInteracted =
          hasContentChanged || interactedAttributes.has(id);
        if (hasContentChanged) {
          setInteractedAttributes((prev) => new Set(prev).add(id));
        }

        // Use the centralized validation function for consistency
        // Pass the interaction state that will be true after the state update
        validateSingleAttribute(
          id,
          currentDescription,
          'onBlur',
          willBeInteracted
        );
      }
    },
    [
      attributes,
      originalAttributes,
      validateSingleAttribute,
      interactedAttributes,
    ]
  );

  const handleAttributeDelete = useCallback(
    (id: number) => {
      // Clear any pending validation timer for deleted attribute
      const existingTimer = validationTimersRef.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
        validationTimersRef.current.delete(id);
      }

      setAttributes((prev) => {
        // Get the description of the attribute being deleted to check for duplicate clearance
        const deletedAttr = prev.find((attr) => attr.id === id);
        const deletedDescription = deletedAttr?.description?.trim() ?? '';

        // Filter out the deleted attribute and update positions
        const remainingAttrs = prev
          .filter((attr) => attr.id !== id)
          .map((attr, index) => ({
            ...attr,
            position: index + 1, // Reset position starting from 1
          }));

        // If the deleted attribute had a description, check if any remaining attributes
        // with duplicate errors for that description should have their errors cleared
        if (deletedDescription.length > 0) {
          return remainingAttrs.map((attr) => {
            // Skip attributes that don't have duplicate errors or have different descriptions
            if (
              attr.valid_type !== 'error' ||
              attr.reason !== DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE ||
              attr.description?.trim() !== deletedDescription
            ) {
              return attr;
            }

            // Check if this attribute still has duplicates with other remaining attributes
            const stillHasDuplicates = remainingAttrs.some((otherAttr) => {
              return (
                otherAttr.id !== attr.id &&
                otherAttr.description?.trim() === deletedDescription &&
                deletedDescription.length > 0
              );
            });

            // If no duplicates remain, clear the duplicate error
            if (!stillHasDuplicates) {
              // Re-validate for other potential errors (like min length)
              const hasBeenInteracted = interactedAttributes.has(attr.id);
              const isNewEmptyAttribute =
                attr.isNewAttribute && deletedDescription.length === 0;

              if (
                !isNewEmptyAttribute &&
                hasBeenInteracted &&
                deletedDescription.trim().length < MIN_ATTRIBUTE_LENGTH
              ) {
                return {
                  ...attr,
                  is_valid: false,
                  reason: MIN_ATTRIBUTE_LENGTH_MESSAGE,
                  valid_type: 'error' as const,
                };
              } else {
                return {
                  ...attr,
                  is_valid: true,
                  reason: '',
                  valid_type: 'success' as const,
                };
              }
            }

            return attr; // Keep duplicate error if other duplicates still exist
          });
        }

        return remainingAttrs;
      });

      setUserHasInteracted(true);
      // Reset hasValidatedButNotSaved when user makes changes
      setHasValidatedButNotSaved(false);

      // Clean up interaction tracking for deleted attribute
      setInteractedAttributes((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [interactedAttributes]
  );

  const handleAttributeAdd = useCallback(() => {
    let newAttributeId: number;

    setAttributes((prev) => {
      const updatedAttributes =
        prev.map((attr, index) => ({
          ...attr,
          position: index + 1,
        })) || [];
      const newAttribute = createNewAttribute(updatedAttributes.length + 1);
      newAttributeId = newAttribute.id;
      return [...updatedAttributes, newAttribute];
    });

    setUserHasInteracted(true);
    // Reset hasValidatedButNotSaved when user makes changes
    setHasValidatedButNotSaved(false);

    return { newAttributeId };
  }, []);

  const handleAttributeReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      setAttributes((prev) => {
        // SIMPLE & ROBUST FIX: Work with positions instead of array indices
        // Sort the array to get the correct visual order, then reorder based on that
        const sortedAttributes = [...prev].sort(
          (a, b) => a.position - b.position
        );

        // Remove source item from sorted array and insert at destination
        const reorderedSorted = [...sortedAttributes];
        const [removed] = reorderedSorted.splice(sourceIndex, 1);
        reorderedSorted.splice(destinationIndex, 0, removed);

        // Recalculate ALL positions to be sequential 1,2,3,4...
        // This ensures positions are always correct and consistent
        const updatedAttributes = reorderedSorted.map((attr, index) => {
          const newPosition = index + 1;
          return {
            ...attr,
            position: newPosition,
          };
        });

        return updatedAttributes;
      });

      setUserHasInteracted(true);
      setHasValidatedButNotSaved(false);
    },
    []
  );

  // Centralized save function with enhanced error handling
  const saveAttributes = useCallback(
    async (
      shouldSave: boolean = false,
      retrigger: boolean = false,
      onSuccess?: () => void
    ) => {
      try {
        let isError = false;

        attributes.forEach((attr) => {
          if (attr.valid_type === 'error') {
            isError = true;
          }
        });

        if (isError) {
          toast({
            title: 'Attributes cannot be saved with errors',
            status: 'error',
            description: 'Please review and fix the errors.',
          });
          return;
        }

        // Filter out empty attributes that haven't been interacted with
        const attributesToSave = attributes.filter((attr) => {
          const isEmpty = attr.description.trim().length === 0;
          return !isEmpty;
        });

        // Clean up attributes - only send necessary fields for new attributes
        const cleanedAttributes = attributesToSave.map((attr) => {
          if (attr.isNewAttribute) {
            return {
              description: attr.description,
              position: attr.position,
            };
          }

          // For existing attributes, send all fields as-is
          return attr;
        });

        // Validate minimum attributes requirement
        const attributesWithDescription = cleanedAttributes.filter(
          (attr) => attr.description.trim().length > 0
        );

        if (attributesWithDescription.length < 3) {
          // MIN_ATTRIBUTES_REQUIRED
          const errorMessage = `You need at least 3 attributes with descriptions to proceed. Currently you have ${attributesWithDescription.length}.`;

          toast({
            title: 'Insufficient attributes',
            status: 'error',
            description: errorMessage,
          });

          return {
            success: false,
            errors: ['At least 3 attributes with descriptions are required.'],
            attributes: undefined,
          };
        }

        const response = await validateAttributesMutation.mutateAsync({
          attributes: cleanedAttributes,
          shouldSave,
          jobId: String(jobId),
          retrigger,
        });

        // The response shape should match your API spec
        const { data, errors } = response?.data || {};

        // If the status is 'pending', the polling will handle the final result
        if (data?.status === 'pending') {
          // If we have partial attributes, merge them with user corrections
          let pendingAttributes = undefined;
          if (data?.attributes) {
            const processedAttributes = processAttributes(data.attributes);
            pendingAttributes = processedAttributes.map((apiAttr) => {
              const currentAttr = attributes.find(
                (attr) => attr.id === apiAttr.id
              );
              const originalAttr = originalAttributes.get(apiAttr.id);

              // Preserve user corrections during pending state
              if (
                currentAttr &&
                originalAttr &&
                !originalAttr.is_valid &&
                currentAttr.description !== originalAttr.description
              ) {
                return currentAttr;
              }

              return apiAttr;
            });
          }

          return {
            success: true,
            attributes: pendingAttributes,
            pending: true,
          };
        } else if (data?.status === 'success' && data?.attributes) {
          const processedAttributes = processAttributes(data.attributes);

          // Preserve user corrections - don't override attributes that user has modified
          const mergedAttributes = processedAttributes.map((apiAttr) => {
            const currentAttr = attributes.find(
              (attr) => attr.id === apiAttr.id
            );
            const originalAttr = originalAttributes.get(apiAttr.id);

            // If user has modified an originally invalid attribute, preserve their changes
            if (
              currentAttr &&
              originalAttr &&
              !originalAttr.is_valid &&
              currentAttr.description !== originalAttr.description
            ) {
              return currentAttr; // Keep user's version
            }

            return apiAttr; // Use API version
          });

          setAttributes(mergedAttributes);

          // Update original attributes map with new API data
          const newOriginalMap = new Map<number, ProcessedAttribute>();
          processedAttributes.forEach((attr) => {
            newOriginalMap.set(attr.id, { ...attr });
          });
          setOriginalAttributes(newOriginalMap);

          // Reset interaction state since attributes are now saved and original state is updated
          setUserHasInteracted(false);
          setHasValidatedButNotSaved(false); // Reset this state after successful save

          // Call onSuccess callback if provided
          if (onSuccess) {
            onSuccess();
          }

          // Check if there are any validation warnings or errors
          // const hasWarnings = processedAttributes.some(
          //   (attr) => attr.valid_type === 'warning'
          // );
          const hasErrors = processedAttributes.some(
            (attr) => attr.valid_type === 'error' || !attr.is_valid
          );

          if (hasErrors) {
            toast({
              title: 'Attributes saved with errors',
              status: 'error',
              description:
                'Some attributes have validation errors. Please review and fix them.',
            });
          }
          // else if (hasWarnings) {
          //   toast({
          //     title: 'Attributes saved with warnings',
          //     status: 'warning',
          //     description:
          //       'Some attributes have validation warnings. Please review them.',
          //   });
          // }

          return { success: true, attributes: mergedAttributes };
        } else {
          // Show specific error messages if available
          if (errors && errors.length > 0) {
            toast({
              title: 'Validation errors',
              status: 'error',
              description: Array.isArray(errors)
                ? errors.join(', ')
                : String(errors),
            });
          }

          return {
            success: false,
            errors: errors || ['Unknown error occurred'],
            attributes: data?.attributes
              ? processAttributes(data.attributes)
              : undefined,
          };
        }
      } catch (error) {
        // Enhanced error handling
        let errorMessage =
          'An unexpected error occurred while saving attributes.';

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        toast({
          title: 'Save failed',
          status: 'error',
          description: errorMessage,
        });

        return {
          success: false,
          errors: [errorMessage],
          attributes: undefined,
        };
      }
    },
    [attributes, validateAttributesMutation, jobId, toast, originalAttributes]
  );

  const resetDirtyState = useCallback(() => {
    setUserHasInteracted(false);
    setHasValidatedButNotSaved(false);
  }, []);

  return {
    attributes,
    isDirty,
    isValid,
    isLoading,
    handleAttributeChange,
    handleAttributeBlur,
    handleAttributeDelete,
    handleAttributeAdd,
    handleAttributeReorder,
    saveAttributes,
    resetDirtyState,
    hasValidatedButNotSaved,
    setValidatedButNotSaved: setHasValidatedButNotSaved,
    interactedAttributes,
    handleAttributeAutoFocus: useCallback((id: number) => {
      // Mark new attributes as interacted when they're auto-focused
      // This enables the special case validation for empty new attributes on blur
      setInteractedAttributes((prev) => new Set(prev).add(id));
    }, []),
  };
};
